import { useReferenceStore } from "@app/store/useReferenceStore";
import type { RequestConfig } from "@app/config/axios/http";
import type { PaginationReturns } from "@src/api/types/PaginationReturns";
import { divisionApi } from "./api.core";
import type {
    ApiDivision,
    ApiDivisionTypeItem,
    Division,
    DivisionExtends,
    DivisionLoadBySectionCodesParams,
    DivisionLoadParams,
} from "./types";

/**
 * Возвращает справочник типов подразделений: сначала session-кэш bootstrap, иначе API. \
 * При успешной загрузке с API записывает результат в {@link useReferenceStore}.
 * @param config опции HTTP-запроса (только при API fallback)
 */
async function resolveDivisionTypes(config?: RequestConfig): Promise<ApiDivisionTypeItem[]> {
    const cached = useReferenceStore.getState().divisionTypes;
    if (cached.length > 0) {
        return cached;
    }

    const types = await divisionApi.loadDivisionTypes(config);
    if (types.length > 0) {
        useReferenceStore.getState().setDivisionTypes(types);
    }
    return types;
}

/**
 * Карта типов по коду. Если в store нет нужных кодов — перезагружает справочник с API.
 * @param codes коды типов из {@link ApiDivision.Type}
 * @param config опции HTTP-запроса (только при API fallback)
 */
async function resolveDivisionTypeMap(codes: number[], config?: RequestConfig): Promise<Map<number, ApiDivisionTypeItem>> {
    let types = useReferenceStore.getState().divisionTypes;
    let map = new Map(types.map((item) => [item.code, item]));

    const hasMissing = codes.some((code) => !map.has(code));
    if (hasMissing) {
        types = await divisionApi.loadDivisionTypes(config);
        if (types.length > 0) {
            useReferenceStore.getState().setDivisionTypes(types);
        }
        map = new Map(types.map((item) => [item.code, item]));
    }

    return map;
}

function serialize(apiData: ApiDivision, includeData?: Partial<DivisionExtends>): Division {
    return {
        ...apiData,
        DivisionType: includeData?.DivisionType,
    };
}

function requireDivisionType(item: ApiDivision, types: Map<number, ApiDivisionTypeItem>): ApiDivisionTypeItem {
    const divisionType = types.get(item.Type);
    if (divisionType == null) {
        throw new Error(
            `divisionServices.load: тип подразделения с кодом ${item.Type} не найден в справочнике (подразделение Code=${item.Code}).`,
        );
    }
    return divisionType;
}

/**
 * Сервисный слой — подразделения.
 */
export const divisionServices = {
    ...divisionApi,

    /**
     * Возвращает подразделения с серверной пагинацией и опциональным expand связей. \
     * При `include: ["DivisionType"]` каждая запись получает `DivisionType` из bootstrap-справочника
     * (при промахе — через API `loadDivisionTypes`). \
     * Если код типа не найден в справочнике — бросает ошибку.
     * @param params фильтр, пагинация и клиентский `include`
     * @param config опции HTTP-запроса
     * @example
     * ```ts
     * const { items } = await divisionServices.load({ include: ["DivisionType"] });
     * items[0].DivisionType?.name
     * ```
     */
    load: async (params: DivisionLoadParams, config?: RequestConfig): Promise<PaginationReturns<Division>> => {
        const { include, ...loadParams } = params;
        const result = await divisionApi.load(loadParams, config);

        if (include == null || !include.includes("DivisionType")) {
            return {
                ...result,
                items: result.items.map((item) => serialize(item)),
            };
        }

        const typeMap = await resolveDivisionTypeMap(
            result.items.map((item) => item.Type),
            config,
        );

        return {
            ...result,
            items: result.items.map((item) =>
                serialize(item, {
                    DivisionType: requireDivisionType(item, typeMap),
                }),
            ),
        };
    },

    /**
     * Справочник типов подразделений. \
     * Сначала берёт данные из bootstrap ({@link useReferenceStore}), иначе — `loadDivisionTypes` API.
     * @param config опции HTTP-запроса (только при API fallback)
     */
    loadDivisionTypes: async (config?: RequestConfig): Promise<ApiDivisionTypeItem[]> => {
        return resolveDivisionTypes(config);
    },

    /**
     * Тип подразделения по коду (`ApiDivision.Type` / `ApiDivisionTypeItem.code`). \
     * Сначала ищет в bootstrap-справочнике; если кода там нет — подгружает справочник через API
     * и обновляет {@link useReferenceStore}. \
     * Если код не найден — бросает ошибку.
     * @param code код типа подразделения
     * @param config опции HTTP-запроса (только при API fallback)
     * @returns элемент справочника
     */
    loadDivisionType: async (code: number, config?: RequestConfig): Promise<ApiDivisionTypeItem> => {
        const fromStore = useReferenceStore.getState().divisionTypes.find((item) => item.code === code);
        if (fromStore != null) {
            return fromStore;
        }

        const types = await divisionApi.loadDivisionTypes(config);
        if (types.length > 0) {
            useReferenceStore.getState().setDivisionTypes(types);
        }

        const fromApi = types.find((item) => item.code === code);
        if (fromApi == null) {
            throw new Error(`divisionServices.loadDivisionType: тип подразделения с кодом ${code} не найден в справочнике.`);
        }

        return fromApi;
    },

    /**
     * Загружает подразделения-участки по кодам участков (`SectionCodes`). \
     * Высокоуровневая альтернатива сырому `sections.loadByFilter({ Codes })` для resolve/batch.
     * @param params коды участков и опциональный `include`
     * @param config опции HTTP-запроса
     * @returns подразделения, у которых `SectionCode` входит в запрошенные коды
     */
    loadBySectionCodes: async (params: DivisionLoadBySectionCodesParams, config?: RequestConfig): Promise<Division[]> => {
        const sectionCodes = [...new Set(params.sectionCodes.filter((code) => code != null && code !== 0))];
        if (sectionCodes.length === 0) {
            return [];
        }

        const { items } = await divisionServices.load(
            {
                flt: { SectionCodes: sectionCodes },
                include: params.include,
            },
            config,
        );
        return items;
    },
} as const;
